import React, { useState, useEffect } from 'react';
import {
  Alert, Avatar, Button, Card, Col, Form, Input, Modal, Row, Space,
  Table, Tag, Typography, Badge, message, Select, Grid
} from 'antd';
import {
  EditOutlined, SafetyOutlined, TeamOutlined, UserAddOutlined,
  UserOutlined, MailOutlined, CrownOutlined, WarningOutlined
} from '@ant-design/icons';
import { useAuth } from '../../../aws/AuthContext';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;
const { confirm } = Modal;

const SeccionUsuarios = () => {

  const { getJwtToken, session } = useAuth();
  const API_URL = process.env.REACT_APP_API_URL;
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const navigate = useNavigate();

  const [usuarios, setUsuarios] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loadingData, setLoadingData] = useState(true);

  const [isModalUserOpen, setIsModalUserOpen] = useState(false);
  const [usuarioEnEdicion, setUsuarioEnEdicion] = useState(null);
  const [formUser] = Form.useForm();
  const [guardando, setGuardando] = useState(false);

  const planActual = session?.empresa?.plan_actual || 'basico';

  const fetchData = async () => {
    setLoadingData(true);
    try {
      const token = await getJwtToken();

      const resRoles = await axios.get(`${API_URL}/v1-roles-permisos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRoles(resRoles.data.roles || []);

      const resUsuarios = await axios.get(`${API_URL}/v1-usuarios?tipo=lista`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (resUsuarios.data && resUsuarios.data.length > 0) {
        setUsuarios(resUsuarios.data);
      } else {
        setUsuarios([{
          id: session?.user?.id,
          nombre: session?.user?.nombre,
          apellidos: session?.user?.apellidos,
          email: session?.user?.email,
          rol_id: session?.rol?.nombre === 'CEO' ? 'ceo' : null,
          status: 'active'
        }]);
      }
    } catch (error) {
      console.error("Error cargando datos:", error);
      message.error("No se pudo cargar la información de los usuarios.");
    } finally {
      setLoadingData(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, []);

  const handleOpenNewUserModal = () => {
    // Solo contamos los usuarios que están ocupando una silla (activos)
    const usuariosActivos = usuarios.filter(u => u.status === 'active').length;

    // Límite Plan Básico (Si quieres forzar el upgrade a Premium)
    if (planActual === 'basico' && usuariosActivos >= 2) {
      Modal.info({
        icon: null, width: 480, centered: true, closable: true, footer: null,
        content: (
          <div style={{ textAlign: 'center', marginTop: '10px' }}>
            <div style={{ background: '#fffbe6', width: '64px', height: '64px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
              <WarningOutlined style={{ fontSize: '32px', color: '#faad14' }} />
            </div>
            <Title level={4} style={{ margin: '0 0 10px 0', color: '#141414' }}>
              Límite del Plan Básico Alcanzado
            </Title>
            <Text style={{ fontSize: '15px', color: '#595959', display: 'block', padding: '0 10px' }}>
              Tu <b>Plan Básico</b> incluye únicamente <b>1 cuenta de empleado</b>.
            </Text>

            <div style={{ background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce3 100%)', padding: '24px', borderRadius: '16px', border: '1px solid #bbf7d0', textAlign: 'center', marginTop: '24px' }}>
              <TeamOutlined style={{ fontSize: '32px', color: '#16a34a', marginBottom: '12px' }} />
              <Text strong style={{ display: 'block', fontSize: '17px', color: '#166534', marginBottom: '8px' }}>
                ¿Necesitas crear más usuarios?
              </Text>
              <Text style={{ fontSize: '13px', color: '#14532d', display: 'block', marginBottom: '20px' }}>
                Mejora al <b>Plan Premium</b> para incluir 6 usuarios y crear cuentas adicionales ilimitadas por solo <b>$25.000 COP/mes</b> c/u.
              </Text>
              <div style={{ display: 'flex', gap: '12px' }}>
                <Button size="large" onClick={() => Modal.destroyAll()} style={{ flex: 1, borderRadius: '8px', fontWeight: '500' }}>
                  Entendido
                </Button>
                <Button type="primary" size="large" style={{ flex: 1, backgroundColor: '#16a34a', borderColor: '#16a34a', borderRadius: '8px', fontWeight: 'bold' }} onClick={() => { Modal.destroyAll(); navigate("/ajustes?tab=suscripcion"); }}>
                  VER PLANES
                </Button>
              </div>
            </div>
          </div>
        )
      });
      return;
    }

    // Límite Plan Premium: ¡AHORA SÍ PERMITE CREAR MÁS DE 6!
    // Si sobrepasa los 6 incluidos, le mostramos una alerta informativa avisándole que se le cobrará $25.000/mes adicionales en su próxima renovación.
    if (planActual === 'premium' && usuariosActivos >= 6) {
      message.info({
        content: `Crearás el usuario #${usuariosActivos + 1}. Este usuario adicional sumará $25.000 COP/mes a tu próxima factura de renovación.`,
        duration: 5
      });
    }

    setUsuarioEnEdicion(null);
    formUser.resetFields();
    setIsModalUserOpen(true);
  };

  const handleSaveUser = async (values) => {
    setGuardando(true);
    try {
      const token = await getJwtToken();

      if (usuarioEnEdicion) {
        const payloadEdicion = {
          id: usuarioEnEdicion.id,
          nombre: values.nombre,
          apellidos: values.apellidos,
          rol_id: values.rol_id
        };

        await axios.patch(`${API_URL}/v1-usuarios`, payloadEdicion, {
          headers: { Authorization: `Bearer ${token}` }
        });

        message.success("Usuario actualizado correctamente.");
      } else {
        const payloadNuevo = {
          nombre: values.nombre,
          apellidos: values.apellidos,
          email: values.email,
          rolId: values.rol_id
        };

        await axios.post(`${API_URL}/v1-usuarios`, payloadNuevo, {
          headers: { Authorization: `Bearer ${token}` }
        });
        message.success("¡Usuario creado! Se le ha enviado un correo con su contraseña temporal.");
      }

      setIsModalUserOpen(false);
      formUser.resetFields();
      fetchData();
    } catch (error) {
      message.error(error.response?.data?.error || "Error al procesar el usuario.");
    } finally {
      setGuardando(false);
    }
  };

  // --- FUNCIÓN PARA ACTIVAR / DESACTIVAR USUARIO ---
  const handleToggleStatus = (record) => {
    const isActivating = record.status === 'inactive';
    const usuariosActivos = usuarios.filter(u => u.status === 'active').length;

    // Validación de Reactivación:
    if (isActivating) {
      // En Básico: No dejamos reactivar si ya tiene sus 2 usuarios activos (1 CEO + 1 Empleado)
      if (planActual === 'basico' && usuariosActivos >= 2) {
        return message.warning("No puedes reactivar este usuario. Has alcanzado el límite de 1 empleado de tu Plan Básico. Pásate a Premium para activar más cuentas.");
      }

      // En Premium: Se le permite reactivar siempre. Si supera los 6 usuarios, le avisamos con una notificación breve.
      if (planActual === 'premium' && usuariosActivos >= 6) {
        message.info(`Al reactivar a ${record.nombre}, tendrás ${usuariosActivos + 1} usuarios activos. El usuario extra sumará $25.000 COP/mes en tu próxima renovación.`);
      }
    }

    confirm({
      title: isActivating ? '¿Reactivar Usuario?' : '¿Desactivar Usuario?',
      content: isActivating
        ? `Se restaurará el acceso al sistema para ${record.nombre}.`
        : `Se bloqueará el acceso al sistema para ${record.nombre}, pero su historial de ventas se mantendrá. Se liberará su cupo en tu plan.`,
      okText: isActivating ? 'Reactivar' : 'Desactivar',
      okType: isActivating ? 'primary' : 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          const token = await getJwtToken();
          const nuevoStatus = isActivating ? 'active' : 'inactive';
          // Petición al backend para actualizar el estado
          await axios.patch(`${API_URL}/v1-usuarios`, { id: record.id, status: nuevoStatus }, { headers: { Authorization: `Bearer ${token}` } });
          message.success(`Usuario ${isActivating ? 'reactivado' : 'desactivado'} con éxito.`);
          fetchData();
        } catch (error) {
          message.error("Error al cambiar el estado del usuario.");
        }
      }
    });
  };

  const columnsUsuarios = [
    {
      title: 'Usuario',
      key: 'user',
      render: (_, record) => {
        const isMe = record.email === session?.user?.email;
        const opacity = record.status === 'inactive' ? 0.5 : 1; // Efecto visual si está inactivo

        return (
          <Space style={{ opacity }}>
            <Avatar
              style={{ backgroundColor: isMe ? '#22BABB' : '#cbd5e1' }}
              icon={isMe && session?.rol?.esCEO ? <CrownOutlined /> : <UserOutlined />}
              src={isMe ? session?.user?.avatar : record.avatar_url}
            />
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <Space>
                <Text strong>{record.nombre} {record.apellidos}</Text>
                {isMe && <Tag color="cyan" style={{ margin: 0, fontSize: '10px' }}>Tú</Tag>}
              </Space>
              <Text type="secondary" style={{ fontSize: '12px' }}>{record.email}</Text>
            </div>
          </Space>
        );
      }
    },
    {
      title: 'Rol Asignado',
      dataIndex: 'rol_id',
      key: 'rol_id',
      render: (rol_id, record) => {
        const isCEO = record.email === session?.user?.email && session?.rol?.esCEO;
        const rolEncontrado = roles.find(r => r.id === rol_id);
        const nombreRol = isCEO ? 'CEO' : (rolEncontrado ? rolEncontrado.nombre : 'Sin Rol');
        const opacity = record.status === 'inactive' ? 0.5 : 1;

        return <Tag color={isCEO ? "gold" : "blue"} icon={<SafetyOutlined />} style={{ opacity }}>{nombreRol}</Tag>;
      }
    },
    {
      title: 'Estado',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Badge status={status === 'active' ? 'success' : 'default'} text={status === 'active' ? 'Activo' : 'Inactivo'} />
      )
    },
    {
      title: 'Acciones',
      key: 'actions',
      align: 'center',
      render: (_, record) => {
        const isMe = record.email === session?.user?.email;
        const isActivo = record.status === 'active';

        return (
          <Space size="small">
            <Button
              type="text"
              icon={<EditOutlined style={{ color: '#22BABB' }} />}
              onClick={() => {
                setUsuarioEnEdicion(record);
                formUser.setFieldsValue(record);
                setIsModalUserOpen(true);
              }}
              disabled={isMe || !isActivo} // No dejar editar usuarios inactivos
            />
            <Button
              type="text"
              danger={isActivo}
              style={{ color: !isActivo ? '#52c41a' : undefined }}
              icon={<TeamOutlined />}
              title={isActivo ? "Desactivar y bloquear acceso" : "Reactivar acceso"}
              disabled={isMe}
              onClick={() => handleToggleStatus(record)}
            />
          </Space>
        );
      }
    }
  ];

  return (
    <div className="fade-in">
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col xs={24} md={16} style={{ marginBottom: isMobile ? 16 : 0 }}>
          <Title level={4} style={{ margin: 0 }}>Usuarios y Accesos</Title>
          <Text type="secondary">Gestiona quiénes tienen acceso a tu organización y qué rol desempeñan.</Text>
        </Col>
        <Col xs={24} md={8} style={{ textAlign: isMobile ? 'left' : 'right' }}>
          <Button
            type="primary"
            icon={<UserAddOutlined />}
            size="large"
            block={isMobile}
            style={{ backgroundColor: '#22BABB', border: 'none' }}
            onClick={handleOpenNewUserModal}
          >
            Nuevo Usuario
          </Button>
        </Col>
      </Row>

      <Card style={{ borderRadius: 12, padding: 0 }} bodyStyle={{ padding: isMobile ? 0 : 24 }}>
        <Table
          columns={columnsUsuarios}
          dataSource={usuarios}
          loading={loadingData}
          rowKey="id"
          pagination={{ pageSize: 5 }}
          scroll={{ x: 700 }}
        />
      </Card>

      <Modal
        title={
          <Space>
            {usuarioEnEdicion ? <EditOutlined /> : <UserAddOutlined />}
            <Text>{usuarioEnEdicion ? `Editar Usuario: ${usuarioEnEdicion.nombre}` : 'Registrar Nuevo Usuario'}</Text>
          </Space>
        }
        open={isModalUserOpen}
        onCancel={() => setIsModalUserOpen(false)}
        onOk={() => formUser.submit()}
        confirmLoading={guardando}
        width={600}
        centered
        okText={usuarioEnEdicion ? "Guardar Cambios" : "Crear Usuario"}
        okButtonProps={{ style: { backgroundColor: '#22BABB' } }}
      >
        {!usuarioEnEdicion && (
          <Alert
            message="Información de Acceso"
            description="Al crear un usuario, el sistema enviará un correo con una contraseña temporal para que pueda ingresar a YUGONUB-POS."
            type="info"
            showIcon
            style={{ marginBottom: 24, marginTop: 10 }}
          />
        )}

        <Form form={formUser} layout="vertical" onFinish={handleSaveUser}>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="nombre" label={<Text strong>Nombre</Text>} rules={[{ required: true, message: 'Requerido' }]}>
                <Input placeholder="Ej: Juan" size="large" prefix={<UserOutlined />} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="apellidos" label={<Text strong>Apellidos</Text>}>
                <Input placeholder="Ej: Pérez" size="large" />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item name="email" label={<Text strong>Correo Electrónico</Text>} rules={[{ required: true, type: 'email', message: 'Correo inválido' }]}>
                <Input placeholder="usuario@yugonub.com" size="large" prefix={<MailOutlined />} disabled={!!usuarioEnEdicion} />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item name="rol_id" label={<Text strong>Asignar Rol</Text>} rules={[{ required: true, message: 'Selecciona un rol' }]}>
                <Select placeholder="Selecciona un rol de la jerarquía" size="large" loading={loadingData}>
                  {roles.filter(r => r.nombre !== 'CEO').map(rol => (
                    <Select.Option key={rol.id} value={rol.id}>
                      {rol.nombre}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default SeccionUsuarios;